import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import { getTenantBySlug, getTenantByDomain } from './firebase/tenants';
import { submitContactForm } from './firebase/database';
import HomePage from './HomePage';
import './App.css';

// Redirect /gym to / on custom domains
export function GymRouteGuard({ children }) {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isCustomDomain = hostname && hostname !== 'localhost' && !hostname.endsWith('.web.app') && !hostname.endsWith('.firebaseapp.com');
  
  if (isCustomDomain) {
    return <Navigate to="/" replace />;
  }
  
  return children;
}

// Simple landing when no tenant slug (e.g. /gym)
function GymFinderLanding() {
  const [slugInput, setSlugInput] = useState('');
  const navigate = useNavigate();
  return (
    <div className="App">
      <main className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">Find your gym</h1>
              <p className="hero-description">
                Your gym&apos;s website is at: <strong>/gym/your-gym-slug</strong>.<br />
                Ask your gym for the link, or enter the slug below.
              </p>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  placeholder="e.g. demusclefactory"
                  value={slugInput}
                  onChange={(e) => setSlugInput(e.target.value)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    borderRadius: 6,
                    border: '1px solid rgba(255,255,255,0.2)',
                    background: 'rgba(255,255,255,0.1)',
                    color: 'white',
                    minWidth: 200
                  }}
                />
                <button
                  type="button"
                  className="cta-btn"
                  onClick={() => {
                    const s = slugInput.trim().toLowerCase().replace(/\s+/g, '-');
                    if (s) navigate(`/gym/${s}`);
                  }}
                >
                  Go to gym
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <footer className="footer">
        <div className="container">
          <p><Link to="/adminlogin" style={{ color: 'rgba(255,255,255,0.7)' }}>Admin login</Link></p>
        </div>
      </footer>
    </div>
  );
}

// Tenant-driven public home + contact (branding, features)
function TenantSiteContent({ tenant, showContactOnly }) {
  const navigate = useNavigate();
  const branding = tenant.branding || {};
  const features = tenant.features || {};
  const siteName = branding.siteName || tenant.name || 'Gym';
  const primaryColor = branding.primaryColor || '#1a1a2e';
  const accentColor = branding.accentColor || '#e94560';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    inquiryType: 'general'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmitMessage = async (e) => {
    e.preventDefault();
    if (!features.contactFormEnabled) return;
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const contactData = {
        ...formData,
        timestamp: Date.now(),
        submissionDate: new Date().toISOString(),
        status: 'new'
      };
      const result = await submitContactForm(tenant.id, contactData);
      if (result.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', message: '', inquiryType: 'general' });
      } else {
        setSubmitStatus('error');
      }
    } catch (_) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToContact = (e) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (showContactOnly) {
    return (
      <div className="App" style={{ ['--tenant-primary']: primaryColor, ['--tenant-accent']: accentColor }}>
        <header className="navbar">
          <div className="nav-container">
            <div className="logo">
              {branding.logoUrl ? (
                <img src={branding.logoUrl} alt={siteName} className="logo-img" />
              ) : (
                <div className="logo-icon">💪</div>
              )}
              <span className="logo-text">{siteName}</span>
            </div>
            <nav className="nav-menu">
              <Link to={`/gym/${tenant.slug}`} className="nav-link">Home</Link>
              <a href="#contact" className="nav-link" onClick={scrollToContact}>Contact</a>
            </nav>
          </div>
        </header>
        <section id="contact" className="contact-section" style={{ padding: '4rem 0' }}>
          <div className="container">
            <h2 className="section-title">Contact {siteName}</h2>
            {features.contactFormEnabled && (
              <>
                {submitStatus === 'success' && <p style={{ color: '#86efac' }}>Thank you! We received your message.</p>}
                {submitStatus === 'error' && <p style={{ color: '#fca5a5' }}>Something went wrong. Please try again.</p>}
                <form onSubmit={handleSubmitMessage} style={{ maxWidth: 500, margin: '0 auto' }}>
                  <input type="text" name="name" placeholder="Name" value={formData.name} onChange={handleInputChange} required className="form-input" style={{ width: '100%', marginBottom: 8 }} />
                  <input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleInputChange} required className="form-input" style={{ width: '100%', marginBottom: 8 }} />
                  <input type="tel" name="phone" placeholder="Phone" value={formData.phone} onChange={handleInputChange} className="form-input" style={{ width: '100%', marginBottom: 8 }} />
                  <textarea name="message" placeholder="Message" value={formData.message} onChange={handleInputChange} required rows={4} className="form-input" style={{ width: '100%', marginBottom: 8 }} />
                  <button type="submit" className="cta-btn" disabled={isSubmitting}>{isSubmitting ? 'Sending...' : 'Send'}</button>
                </form>
              </>
            )}
          </div>
        </section>
        <footer className="footer">
          <div className="container"><p>&copy; {new Date().getFullYear()} {siteName}</p></div>
        </footer>
      </div>
    );
  }

  return (
    <div className="App" style={{ ['--tenant-primary']: primaryColor, ['--tenant-accent']: accentColor }}>
      <header className="navbar">
        <div className="nav-container">
          <div className="logo">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt={siteName} className="logo-img" />
            ) : (
              <div className="logo-icon">💪</div>
            )}
            <span className="logo-text">{siteName}</span>
          </div>
          <nav className="nav-menu">
            <a href="#home" className="nav-link active" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Home</a>
            <a href="#timings" className="nav-link">Timings</a>
            <a href="#contact" className="nav-link" onClick={scrollToContact}>Contact</a>
          </nav>
        </div>
      </header>

      <main className="hero-section">
        <div className="hero-container">
          <div className="hero-content">
            <div className="hero-text">
              <div className="tagline">FIND YOUR ENERGY</div>
              <h1 className="hero-title">
                <div className="title-line-1">MAKE YOUR BODY</div>
                <div className="title-line-2"><span className="title-highlight">FIT & PERFECT</span></div>
              </h1>
              <p className="hero-description">
                <div className="desc-line">Transform Your Body</div>
                <div className="desc-line">Strength Meets Determination</div>
              </p>
            </div>
          </div>
        </div>
      </main>

      <section id="timings" className="gym-timings-section">
        <div className="container">
          <div className="timings-header">
            <h2 className="gym-name">{siteName}</h2>
            <p className="gym-subtitle">The Gym With The Motive</p>
          </div>
          <div className="timings-content">
            <div className="timings-card">
              <h3 className="timings-title">🕐 GYM TIMINGS</h3>
              <div className="timing-slots">
                <div className="timing-slot unisex"><div className="time-period">5:00 AM - 8:30 AM</div><div className="session-type unisex-session">UNISEX</div></div>
                <div className="timing-slot female"><div className="time-period">8:30 AM - 11:30 AM</div><div className="session-type female-session">ONLY FEMALE</div></div>
                <div className="timing-slot unisex"><div className="time-period">11:30 AM - 3:00 PM</div><div className="session-type unisex-session">UNISEX</div></div>
                <div className="timing-slot female"><div className="time-period">3:00 PM - 5:15 PM</div><div className="session-type female-session">ONLY FEMALE</div></div>
                <div className="timing-slot unisex"><div className="time-period">5:00 PM - 11:00 PM</div><div className="session-type unisex-session">UNISEX</div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {features.showClasses && (
        <section className="classes-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Our Featured Classes</h2>
            </div>
            <div className="classes-grid">
              <div className="class-card"><div className="class-icon">🏋️</div><h3 className="class-title">Power Lifting</h3><p className="class-description">Build strength with expert guidance.</p></div>
              <div className="class-card"><div className="class-icon">💪</div><h3 className="class-title">Workout</h3><p className="class-description">Full-body fitness programs.</p></div>
            </div>
          </div>
        </section>
      )}

      {features.showTrainers && (
        <section className="team-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Our Trainers</h2>
            </div>
            <div className="trainers-grid">
              <div className="trainer-card"><div className="trainer-image"><img src="/images/profile.jpg" alt="Trainer" className="trainer-photo" /></div><div className="trainer-info"><h4 className="trainer-name">Team</h4><p className="trainer-specialty">Trainer</p></div></div>
            </div>
          </div>
        </section>
      )}

      {features.contactFormEnabled && (
        <section id="contact" className="contact-section">
          <div className="container">
            <h2 className="section-title">Contact Us</h2>
            {submitStatus === 'success' && <p style={{ color: '#86efac', marginBottom: '1rem' }}>Thank you! We received your message.</p>}
            {submitStatus === 'error' && <p style={{ color: '#fca5a5', marginBottom: '1rem' }}>Something went wrong. Please try again.</p>}
            <form onSubmit={handleSubmitMessage} style={{ maxWidth: 500, margin: '0 auto' }}>
              <input type="text" name="name" placeholder="Name" value={formData.name} onChange={handleInputChange} required className="form-input" style={{ width: '100%', marginBottom: 8 }} />
              <input type="email" name="email" placeholder="Email" value={formData.email} onChange={handleInputChange} required className="form-input" style={{ width: '100%', marginBottom: 8 }} />
              <input type="tel" name="phone" placeholder="Phone" value={formData.phone} onChange={handleInputChange} className="form-input" style={{ width: '100%', marginBottom: 8 }} />
              <textarea name="message" placeholder="Message" value={formData.message} onChange={handleInputChange} required rows={4} className="form-input" style={{ width: '100%', marginBottom: 8 }} />
              <button type="submit" className="cta-btn" disabled={isSubmitting}>{isSubmitting ? 'Sending...' : 'Send message'}</button>
            </form>
          </div>
        </section>
      )}

      <footer className="footer">
        <div className="container"><p>&copy; {new Date().getFullYear()} {siteName}</p></div>
      </footer>
    </div>
  );
}

function TenantPublicHome({ contact }) {
  const { tenantSlug } = useParams();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!tenantSlug) {
      setTenant(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    getTenantBySlug(tenantSlug).then((result) => {
      if (cancelled) return;
      if (result.success && result.tenant) {
        setTenant(result.tenant);
      } else {
        setNotFound(true);
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [tenantSlug]);

  if (!tenantSlug) {
    return <GymFinderLanding />;
  }
  if (loading) {
    return <div className="App" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><p>Loading...</p></div>;
  }
  if (notFound || !tenant) {
    return (
      <div className="App" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '1rem' }}>
        <h1>Gym not found</h1>
        <p>The gym you&apos;re looking for doesn&apos;t exist or is not active.</p>
        <Link to="/gym" className="cta-btn">Back to gym finder</Link>
      </div>
    );
  }
  if (contact) return <TenantSiteContent tenant={tenant} showContactOnly />;
  return <HomePage tenant={tenant} />;
}

// Default tenant slug when not on a custom domain (localhost / *.web.app)
const DEFAULT_TENANT_SLUG = 'demusclefactory';

// Resolve tenant by custom domain or default slug; render home or contact at "/" and "/contact"
export function DomainAwareHome() {
  const location = useLocation();
  const pathname = location.pathname || '';
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isCustomDomain = hostname && hostname !== 'localhost' && !hostname.endsWith('.web.app') && !hostname.endsWith('.firebaseapp.com');

  const [tenant, setTenant] = useState(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (isCustomDomain) {
      getTenantByDomain(hostname).then((result) => {
        if (cancelled) return;
        if (result.success && result.tenant) setTenant(result.tenant);
        setChecked(true);
      });
    } else {
      getTenantBySlug(DEFAULT_TENANT_SLUG).then((result) => {
        if (cancelled) return;
        if (result.success && result.tenant) setTenant(result.tenant);
        setChecked(true);
      });
    }
    return () => { cancelled = true; };
  }, [hostname, isCustomDomain]);

  if (!tenant && checked) {
    return <Navigate to="/gym" replace />;
  }
  if (pathname === '/contact') {
    return <TenantSiteContent tenant={tenant} showContactOnly />;
  }
  return <HomePage tenant={tenant} />;
}

export default TenantPublicHome;
